import { GraphQLClient } from 'graphql-request';

import { IChannel } from '../../channels/types';
import { detectCountryForCheckout } from '../../country/detect-country-for-checkout';
import {
  CheckoutAddProductLineDocument,
  CheckoutAddProductLineMutation,
  CheckoutAddProductLineMutationVariables,
  CheckoutAddProductLinesDocument,
  CheckoutAddProductLinesMutation,
  CheckoutAddProductLinesMutationVariables,
  CheckoutAddPromoCodeDocument,
  CheckoutAddPromoCodeMutation,
  CheckoutAddPromoCodeMutationVariables,
  CheckoutByIdDocument,
  CheckoutByIdQuery,
  CheckoutByIdQueryVariables,
  CheckoutDetailsFragment,
  CheckoutLineInput,
  CheckoutLineUpdateDocument,
  CheckoutLineUpdateMutation,
  CheckoutLineUpdateMutationVariables,
  CheckoutShippingMethodUpdateDocument,
  CheckoutShippingMethodUpdateMutation,
  CheckoutShippingMethodUpdateMutationVariables,
  CreateCheckoutDocument,
  CreateCheckoutMutation,
  CreateCheckoutMutationVariables,
  LanguageCodeEnum,
  MetadataInput,
  RemoveProductFromCheckoutDocument,
  RemoveProductFromCheckoutMutation,
  RemoveProductFromCheckoutMutationVariables,
} from '../../saleor-graphql';

const CHECKOUT_STORAGE_KEY = 'boc:checkout';

export interface CartConfig {
  channel: IChannel;
  locale: LanguageCodeEnum;
  userEmail?: string;
}

export class CartAPI {
  private client: GraphQLClient;
  private config: CartConfig;

  constructor(client: GraphQLClient, config: CartConfig) {
    this.client = client;
    this.config = config;
  }

  getCheckoutId(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(CHECKOUT_STORAGE_KEY);
      if (!stored) return null;

      const checkouts = JSON.parse(stored) as Record<string, string>;
      return checkouts[this.config.channel.slug] || null;
    } catch {
      return null;
    }
  }

  setCheckoutId(checkoutId: string): void {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(CHECKOUT_STORAGE_KEY);
      const checkouts = stored ? (JSON.parse(stored) as Record<string, string>) : {};
      checkouts[this.config.channel.slug] = checkoutId;
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkouts));
    } catch {
      // Fail silently
    }
  }

  clearCheckoutId(): void {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(CHECKOUT_STORAGE_KEY);
      if (!stored) return;

      const checkouts = JSON.parse(stored) as Record<string, string>;
      delete checkouts[this.config.channel.slug];
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkouts));
    } catch {
      // Fail silently
    }
  }

  async getCheckout(): Promise<CheckoutDetailsFragment | null> {
    const checkoutId = this.getCheckoutId();
    if (!checkoutId) return null;

    const response = await this.client.request<CheckoutByIdQuery, CheckoutByIdQueryVariables>(
      CheckoutByIdDocument,
      {
        id: checkoutId,
        locale: this.config.locale,
      }
    );

    return response.checkout || null;
  }

  async createCheckout(
    variantId: string,
    quantity: number = 1,
    metadata?: MetadataInput[]
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const lines: CheckoutLineInput[] = [
      {
        variantId,
        quantity,
        ...(metadata && { metadata }),
      },
    ];

    // Detect country from CloudFront headers or timezone
    const countryCode = await detectCountryForCheckout(this.config.channel);

    return this.createCheckoutWithLines(lines, this.config.userEmail, countryCode);
  }

  async createCheckoutWithLines(
    lines: CheckoutLineInput[],
    email?: string,
    countryCode?: string
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    try {
      const response = await this.client.request<
        CreateCheckoutMutation,
        CreateCheckoutMutationVariables
      >(CreateCheckoutDocument, {
        channel: this.config.channel.slug,
        email: email || this.config.userEmail,
        lines,
        locale: this.config.locale,
        countryCode: countryCode || this.config.channel.defaultCountry,
      });

      if (response.checkoutCreate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutCreate.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
        };
      }

      const checkout = response.checkoutCreate?.checkout;
      if (checkout) {
        this.setCheckoutId(checkout.id);

        // The shipping method is chosen explicitly by the shopper on the
        // checkout shipping step -- never auto-selected here.
        return { checkout, errors: [] };
      }

      return { checkout: null, errors: ['FAILED_TO_CREATE_CHECKOUT'] };
    } catch (error) {
      console.error('Failed to create checkout:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async addItem(
    variantId: string,
    quantity: number = 1,
    metadata?: MetadataInput[],
    forceNewLine?: boolean
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.getCheckoutId();

    if (!checkoutId) {
      return this.createCheckout(variantId, quantity, metadata);
    }

    try {
      // If metadata is provided, use the lines API which supports metadata
      if (metadata) {
        const lines: CheckoutLineInput[] = [
          {
            variantId,
            quantity,
            metadata,
            ...(forceNewLine && { forceNewLine }),
          },
        ];

        const response = await this.client.request<
          CheckoutAddProductLinesMutation,
          CheckoutAddProductLinesMutationVariables
        >(CheckoutAddProductLinesDocument, {
          checkoutId,
          lines,
          locale: this.config.locale,
        });

        if (response.checkoutLinesAdd?.errors.length) {
          return {
            checkout: null,
            errors: response.checkoutLinesAdd.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
          };
        }

        return {
          checkout: response.checkoutLinesAdd?.checkout || null,
          errors: [],
        };
      }

      // Otherwise use the simpler single-line API
      const response = await this.client.request<
        CheckoutAddProductLineMutation,
        CheckoutAddProductLineMutationVariables
      >(CheckoutAddProductLineDocument, {
        checkoutId,
        variantId,
        quantity,
        locale: this.config.locale,
      });

      if (response.checkoutLinesAdd?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutLinesAdd.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
        };
      }

      return {
        checkout: response.checkoutLinesAdd?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async updateItem(
    lineId: string,
    quantity?: number,
    metadata?: MetadataInput[]
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const response = await this.client.request<
        CheckoutLineUpdateMutation,
        CheckoutLineUpdateMutationVariables
      >(CheckoutLineUpdateDocument, {
        checkoutId,
        lineId,
        quantity,
        metadata: metadata || [], // Always pass array, never undefined/null
        locale: this.config.locale,
      });

      if (response.checkoutLinesUpdate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutLinesUpdate.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
        };
      }

      return {
        checkout: response.checkoutLinesUpdate?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to update cart item:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async removeItem(
    lineId: string
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const response = await this.client.request<
        RemoveProductFromCheckoutMutation,
        RemoveProductFromCheckoutMutationVariables
      >(RemoveProductFromCheckoutDocument, {
        id: checkoutId,
        lineIds: [lineId],
        locale: this.config.locale,
      });

      if (response.checkoutLinesDelete?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutLinesDelete.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
        };
      }

      const checkout = response.checkoutLinesDelete?.checkout || null;

      // Clear checkout if empty
      if (checkout && checkout.lines.length === 0) {
        this.clearCheckoutId();
      }

      return { checkout, errors: [] };
    } catch (error) {
      console.error('Failed to remove cart item:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async addPromoCode(
    promoCode: string
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const response = await this.client.request<
        CheckoutAddPromoCodeMutation,
        CheckoutAddPromoCodeMutationVariables
      >(CheckoutAddPromoCodeDocument, {
        checkoutId,
        promoCode,
        locale: this.config.locale,
      });

      if (response.checkoutAddPromoCode?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutAddPromoCode.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
        };
      }

      return {
        checkout: response.checkoutAddPromoCode?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to add promo code:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async setShippingMethod(
    shippingMethodId: string
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const response = await this.client.request<
        CheckoutShippingMethodUpdateMutation,
        CheckoutShippingMethodUpdateMutationVariables
      >(CheckoutShippingMethodUpdateDocument, {
        shippingMethodId,
        id: checkoutId,
        locale: this.config.locale,
      });

      if (response.checkoutShippingMethodUpdate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutShippingMethodUpdate.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
        };
      }

      return {
        checkout: response.checkoutShippingMethodUpdate?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to set shipping method:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }
}
